"use client";

import Link from "next/link";
import { Bell, BellRing } from "lucide-react";
import { useCustomer } from "@/context/CustomerContext";
import { useUnreadNotifications } from "@/context/NotificationContext";
import { formatBadgeCount } from "@/lib/notificationBadge";

type Variant = "bar" | "floating";

// Kelas literal per varian (Tailwind memindai string utuh). Tiap varian kembar
// dengan ikon keranjang di sebelahnya — kotak, warna ikon, dan gaya badge-nya.
const BOX: Record<Variant, string> = {
  // Header gradien tema: beranda, kategori, merek, cari, promo, rail, PO segera ditutup.
  bar: "relative grid h-10 w-10 shrink-0 place-items-center",
  // Header melayang di atas foto (detail produk): tombol bulat.
  floating:
    "relative grid h-10 w-10 shrink-0 place-items-center rounded-full bg-white/90 shadow-sm backdrop-blur",
};
const ICON_TONE: Record<Variant, string> = { bar: "text-white", floating: "text-ink" };
const MUTED_TONE: Record<Variant, string> = { bar: "text-white/45", floating: "text-ink/30" };
const BADGE: Record<Variant, string> = {
  bar: "absolute right-0 top-0 grid h-4 min-w-4 place-items-center rounded-full border border-shopee bg-white px-1 text-[9px] font-bold leading-none text-shopee",
  floating:
    "absolute -right-0.5 -top-0.5 grid h-5 min-w-5 place-items-center rounded-full bg-shopee px-1 text-[10px] font-bold text-white",
};
const ICON_SIZE: Record<Variant, number> = { bar: 22, floating: 20 };

/**
 * Lonceng notifikasi pembeli di header, tepat di KIRI ikon keranjang
 * (permintaan pemilik 2026-09-24 — sebelumnya slot "Notif" di navigasi bawah).
 *
 * Presentasional saja: angkanya dari `NotificationProvider` (mesin sekali pasang
 * di layout), jadi header yang remount tiap navigasi tak memicu query maupun
 * langganan realtime baru. Tamu / sesi yang belum termuat: ikon pudar yang tak
 * bisa diketuk (perilaku slot lama) di kotak berukuran SAMA — header tak
 * bergeser saat status login termuat.
 */
export function NotificationBell({ variant }: { variant: Variant }) {
  const { customer } = useCustomer();
  const unread = useUnreadNotifications();
  const size = ICON_SIZE[variant];

  if (!customer) {
    return (
      <span aria-hidden className={`${BOX[variant]} ${MUTED_TONE[variant]}`}>
        <Bell size={size} />
      </span>
    );
  }

  return (
    <Link
      href="/notifications"
      // Header ini ada di hampir semua halaman belanja; prefetch viewport = satu
      // request RSC ke origin per halaman yang dibuka (target dinamis). Fetch
      // hanya saat lonceng benar-benar diketuk — idiom navigasi bawah.
      prefetch={false}
      aria-label={unread > 0 ? `Notifikasi, ${unread} belum dibaca` : "Notifikasi"}
      className={`${BOX[variant]} ${ICON_TONE[variant]}`}
    >
      {unread > 0 ? <BellRing size={size} /> : <Bell size={size} />}
      {unread > 0 ? <span className={BADGE[variant]}>{formatBadgeCount(unread)}</span> : null}
    </Link>
  );
}
